import type { Language } from "./language";

export type PriceSuffix = "none" | "from" | "perImage" | "perHour";

export type ServiceItem = {
  name: string;
  detail: string;
  huf: number;
  suffix?: PriceSuffix;
  highlight?: boolean;
  colorBtn?: boolean;
};

export const baseUrl = "https://ivisiondesign.hu";

const common = {
  email: "workivett@gmail.com",
  phone: "+36 70 285 8571",
  site: "ivisiondesign.hu",
  designer: "Lakatos Ivett",
};

export const nav = {
  hu: {
    links: [
      { label: "Rólam", href: "#about" },
      { label: "Szolgáltatások", href: "#services" },
      { label: "Vélemények", href: "#testimonials" },
      { label: "Portfólió", href: "#portfolio" },
      { label: "Színek", href: "#colors" },
      { label: "Kapcsolat", href: "#contact" },
    ],
    cta: "Konzultáció kérése",
    menu: "Menü",
  },
  en: {
    links: [
      { label: "About", href: "#about" },
      { label: "Services", href: "#services" },
      { label: "Reviews", href: "#testimonials" },
      { label: "Portfolio", href: "#portfolio" },
      { label: "Colors", href: "#colors" },
      { label: "Contact", href: "#contact" },
    ],
    cta: "Request a consultation",
    menu: "Menu",
  },
} satisfies Record<Language, unknown>;

export const hero = {
  hu: {
    badge: "Lakatos Ivett – Creative Designer",
    titleTop: "Látomásból",
    titleAccent: "Valóság.",
    text: "Prémium vizuális megoldások AI integrációval. Branding, arculattervezés és grafika, amelyek segítenek a márkádnak kiemelkedni és növekedni.",
    primary: "Munkáim megtekintése",
    secondary: "Konzultáció kérése",
    stats: [
      { value: "3+", label: "Év tapasztalat" },
      { value: "100%", label: "Szenvedély" },
    ],
    scroll: "Scroll",
  },
  en: {
    badge: "Lakatos Ivett – Creative Designer",
    titleTop: "From Vision",
    titleAccent: "To Reality.",
    text: "Premium visual solutions with AI integration. Branding, identity design, and graphics that help your brand stand out and grow.",
    primary: "View my work",
    secondary: "Request a consultation",
    stats: [
      { value: "3+", label: "Years experience" },
      { value: "100%", label: "Passion" },
    ],
    scroll: "Scroll",
  },
} satisfies Record<Language, unknown>;

export const about = {
  hu: {
    label: "Rólam",
    intro: "Gyerekkorom óta mindig is kreatív voltam és szenvedélyesen vonzódtam a művészetekhez. Magyar vagyok, és a célom a tervezéssel, hogy segítsek az embereknek növekedni és kiemelkedni a márkáikkal.",
    storyTitle: "A történetem",
    story: [
      "18 éves korom óta szenvedélyesen foglalkozom grafikai tervezéssel. A szabadidőm nagy részét az Adobe Photoshop és Illustrator programokban töltöm, és a projektjeimhez az inspirációt főként a Canva és a kedvenc Behance tartalomkészítőimtől merítem.",
      "Imádom a munkám, mert gyorsan rájöttem, hogy a boldog mindennapi élet kulcsa az egyensúlyban lévő munka, amely eleinte csak hobbiként indult, mára pedig a hivatásommá vált.",
      "Számomra a grafikai tervezés nem csak munka – hanem lehetőség arra, hogy mások márkáit láthatóvá és emlékezetessé tegyem. Minden projekt egy új történet, amit vizuálisan mesélek el.",
    ],
    coursesTitle: "Képzések & Tanulmányok",
    courses: [
      "Grafikai tervező mesterkurzus – középhaladó szint",
      "Teljes arculattervezési folyamat",
      "Szabadúszó mesterkurzus",
      "Online grafikai tanfolyam",
      "Fotográfiai tanfolyam",
    ],
    webler: "Webler Oktatóiskola – Művészeti grafikus szakmai képzés (folyamatban, szóbeli vizsga: 2026. június 02.)",
    profileAlt: "Profilkép",
    experienceLabel: "Év szakmai tapasztalat",
    timelineTitle: "Szakmai útvonalam",
    timeline: [
      { year: "2022", title: "Fotográfia & Grafika", desc: "Fotográfiai tanfolyam, majd online grafikai képzés. Bár a WordPress és a kódolás érdekelt, rájöttem: a logó és márkatervezés az igazi szerelmem." },
      { year: "2024", title: "Szabadúszó kezdetek", desc: "Elindítottuk a családi vállalkozásunkat, a Party Land-et. Én készítettem az arculatot, a közösségi média tartalmakat és kezeltem a hirdetéseket." },
      { year: "2025 eleje", title: "Ügynökség", desc: "META és TikTok fiókok menedzselése, grafikai anyagok készítése (e-book, posztok, hirdetések) ügyfelek számára." },
      { year: "2025–", title: "Honlap.hu", desc: "A Honlap.hu grafikai és UI/UX design csapatát erősítem, és ügyfeleknek elkészítem, újratervezem az arculatukat és logójukat." },
    ],
    values: [
      { icon: "ri-focus-3-line", title: "Fókusz", desc: "Minden döntés az ügyfél céljait szolgálja." },
      { icon: "ri-time-line", title: "Határidők", desc: "Pontosság és átláthatóság minden fázisban." },
      { icon: "ri-award-line", title: "Minőség", desc: "Kompromisszumok nélkül, minden részletben." },
    ],
    cta: "Írj nekem",
  },
  en: {
    label: "About",
    intro: "I have been creative since childhood and deeply drawn to the arts. I am Hungarian, and my goal with design is to help people grow and stand out with their brands.",
    storyTitle: "My story",
    story: [
      "I have been passionate about graphic design since I was 18. I spend much of my free time in Adobe Photoshop and Illustrator, and I often draw inspiration from Canva and my favorite Behance creators.",
      "I love my work because I quickly realized that the key to a happy everyday life is balanced work. What started as a hobby has become my profession.",
      "For me, graphic design is not just work. It is an opportunity to make other people's brands visible and memorable. Every project is a new story that I tell visually.",
    ],
    coursesTitle: "Courses & Studies",
    courses: [
      "Graphic design masterclass – intermediate level",
      "Complete brand identity design process",
      "Freelance masterclass",
      "Online graphic design course",
      "Photography course",
    ],
    webler: "Webler Training School – Professional art graphic design training (in progress, oral exam: June 2, 2026)",
    profileAlt: "Profile picture",
    experienceLabel: "Years of professional experience",
    timelineTitle: "My professional path",
    timeline: [
      { year: "2022", title: "Photography & Graphics", desc: "Photography course followed by online graphic design training. Although WordPress and coding interested me, I realized logo and brand design are my true passion." },
      { year: "2024", title: "Freelance beginnings", desc: "We launched our family business, Party Land. I created the visual identity, social media content, and managed the ads." },
      { year: "Early 2025", title: "Agency", desc: "Managing META and TikTok accounts, creating graphic materials such as e-books, posts, and ads for clients." },
      { year: "2025–", title: "Honlap.hu", desc: "I strengthen the graphic and UI/UX design team at Honlap.hu and create or redesign clients' identities and logos." },
    ],
    values: [
      { icon: "ri-focus-3-line", title: "Focus", desc: "Every decision serves the client's goals." },
      { icon: "ri-time-line", title: "Deadlines", desc: "Accuracy and transparency in every phase." },
      { icon: "ri-award-line", title: "Quality", desc: "No compromises, in every detail." },
    ],
    cta: "Write to me",
  },
} satisfies Record<Language, unknown>;

export const services = {
  hu: {
    aria: "Szolgáltatások és árak",
    eyebrow: "Szolgáltatások & Árak",
    title: "Amit kínálok",
    intro: "Átlátható árak, prémium minőség. Logó tervezés, arculattervezés, social media grafika, AI illusztráció és nyomdai tervek – 3 év szakmai tapasztalattal.",
    best: "Legjobb ajánlat",
    colorCta: "Generálj logó színt!",
    beautyTab: "Szépségiparban dolgozol?",
    quoteLabel: "Egyedi árajánlat",
    required: "Kötelező",
    optional: "Opcionális",
    beautyFooter: "Az ár a kiválasztott elemektől függ. Kérj egyedi árajánlatot!",
    beautyRequired: "Kötelező elemek: névjegykártya, utóápolási kártya/füzet, ajándékkártya, árlista",
    requestQuote: "Árajánlatot kérek",
    discountTitle: "Mennyiségi kedvezmények",
    conditionsTitle: "Üzleti feltételek",
    taxNote: "Az árak forintban értendők és alanyi adómentesek (nettó).",
    processTitle: "A munkafolyamat",
    processIntro: "Lépésről lépésre, átláthatóan.",
    bottomCta: "Kérj árajánlatot",
    from: "-tól",
    perImage: "/ kép",
    perHour: "/ óra",
    categories: [
      {
        icon: "ri-brush-line",
        title: "Arculattervezés & Branding",
        desc: "Komplett vizuális identitás, amely megkülönböztet és emlékezetes marad.",
        items: [
          { name: "Logó Design Pro", detail: "3 koncepció, 3 módosítás, vektoros forrásfájlok, + Ajándék vízjel", huf: 55000, colorBtn: true },
          { name: "Arculati Kézikönyv", detail: "Tipográfia, színkódok, használati útmutató, moodboard", huf: 75000 },
          { name: "Social Media poszt", detail: "5 db egyedi grafika, arculati stílusban", huf: 25000 },
          { name: "Full Brand Identity Csomag", detail: "Logó terv (és variációi) + Kézikönyv + Névjegykártya + Social Media kit (borítókép, profilkép, highlights borítók) + Ajándék matrica design", huf: 140000, highlight: true },
        ],
      },
      {
        icon: "ri-layout-masonry-line",
        title: "Digitális & Print Egységárak",
        desc: "Nyomtatott és digitális anyagok, amelyek minden felületen hatásosak.",
        items: [
          { name: "Névjegykártya tervezés", detail: "2 variáció, nyomdakész fájlok", huf: 22000 },
          { name: "Szórólap / Árlista design", detail: "1 oldal, nyomdakész", huf: 20000 },
          { name: "Plakát / Molinó tervezés", detail: "Egyedi méretben, nyomdakész", huf: 25000 },
          { name: "Ajándékutalvány / Névtábla / Menükártya", detail: "Egyedi tervezés", huf: 15000 },
          { name: "Egyedi termékdesign", detail: "Textil ruházat, bögre, mágnes és egyéb termékek egyedi dizájnja", huf: 25000 },
        ],
      },
      {
        icon: "ri-sparkling-line",
        title: "AI & Prémium Fotómanipuláció",
        desc: "Mesterséges intelligencia alapú vizuális megoldások, high-end minőségben.",
        items: [
          { name: "Egyedi AI Illusztráció / Koncepcióalkotás", detail: "Teljesen egyedi, brandelt AI vizuál", huf: 25000, suffix: "from" },
          { name: "Termékfotó AI környezetbe helyezés", detail: "Styling és kompozíció, képenként", huf: 20000, suffix: "perImage" },
          { name: "High-end retusálás és objektum csere", detail: "Professzionális képszerkesztés", huf: 12000, suffix: "perHour" },
        ],
      },
    ],
    beautyItems: [
      { name: "Logó", required: false },
      { name: "Névjegykártya", required: true },
      { name: "Utóápolási kártya / füzet", required: true },
      { name: "Ajándékkártya", required: true },
      { name: "Árlista", required: true },
      { name: "Vendégfüzet", required: false },
      { name: "Arculat", required: false },
    ],
    beautyTitle: "Szépségipar Csomag",
    beautyDesc: "Szépségszalonok, körmösök, fodrászok, masszazsőrök és minden szépségiparban dolgozó számára",
    discounts: [
      { range: "5–10 egység rendelése esetén", value: "−15% kedvezmény" },
      { range: "10 egység felett vagy havi együttműködésnél", value: "−25% kedvezmény" },
    ],
    conditions: [
      { icon: "ri-refresh-line", text: "Minden ár 3 módosítási kört tartalmaz" },
      { icon: "ri-time-line", text: "További javítások: 10 000 Ft / óra" },
      { icon: "ri-flashlight-line", text: "Sürgősségi felár (48 órán belül): +50%" },
      { icon: "ri-price-tag-3-line", text: "Minimum projekt díj: 20 000 Ft" },
    ],
    process: [
      { num: "01", title: "Konzultáció", desc: "Megismerjük a céljaidat, a célközönségedet és az elvárásaidat." },
      { num: "02", title: "Tervezés", desc: "Kreatív koncepciók kidolgozása, moodboard és első vázlatok." },
      { num: "03", title: "Iteráció", desc: "Visszajelzések alapján finomítás, amíg tökéletes nem lesz." },
      { num: "04", title: "Átadás", desc: "Végleges fájlok, útmutató és hosszú távú támogatás." },
    ],
  },
  en: {
    aria: "Services and pricing",
    eyebrow: "Services & Pricing",
    title: "What I offer",
    intro: "Transparent pricing, premium quality. Logo design, brand identity, social media graphics, AI illustration, and print design with 3 years of professional experience.",
    best: "Best value",
    colorCta: "Generate logo colors",
    beautyTab: "Working in beauty?",
    quoteLabel: "Custom quote",
    required: "Required",
    optional: "Optional",
    beautyFooter: "The price depends on the selected items. Request a custom quote.",
    beautyRequired: "Required items: business card, aftercare card/booklet, gift card, price list",
    requestQuote: "Request a quote",
    discountTitle: "Volume discounts",
    conditionsTitle: "Business terms",
    taxNote: "English prices are informational EUR estimates based on the daily exchange rate. Final invoices are issued tax-exempt in HUF.",
    processTitle: "The workflow",
    processIntro: "Step by step, with full transparency.",
    bottomCta: "Request a quote",
    from: "from",
    perImage: "/ image",
    perHour: "/ hour",
    categories: [
      {
        icon: "ri-brush-line",
        title: "Brand Identity & Branding",
        desc: "A complete visual identity that makes your brand distinctive and memorable.",
        items: [
          { name: "Logo Design Pro", detail: "3 concepts, 3 revision rounds, vector source files, + free watermark", huf: 55000, colorBtn: true },
          { name: "Brand Guidelines", detail: "Typography, color codes, usage guide, moodboard", huf: 75000 },
          { name: "Social Media Post", detail: "5 custom graphics in your brand style", huf: 25000 },
          { name: "Full Brand Identity Package", detail: "Logo design and variations + guidelines + business card + social media kit + free sticker design", huf: 140000, highlight: true },
        ],
      },
      {
        icon: "ri-layout-masonry-line",
        title: "Digital & Print Unit Prices",
        desc: "Printed and digital materials that work beautifully across every surface.",
        items: [
          { name: "Business Card Design", detail: "2 variations, print-ready files", huf: 22000 },
          { name: "Flyer / Price List Design", detail: "1 page, print-ready", huf: 20000 },
          { name: "Poster / Banner Design", detail: "Custom size, print-ready", huf: 25000 },
          { name: "Gift Voucher / Name Sign / Menu Card", detail: "Custom design", huf: 15000 },
          { name: "Custom Product Design", detail: "Custom design for textile clothing, mugs, magnets, and other products", huf: 25000 },
        ],
      },
      {
        icon: "ri-sparkling-line",
        title: "AI & Premium Photo Manipulation",
        desc: "AI-powered visual solutions with a high-end finish.",
        items: [
          { name: "Custom AI Illustration / Concept Creation", detail: "Fully custom, branded AI visual", huf: 25000, suffix: "from" },
          { name: "Product Photo in AI Environment", detail: "Styling and composition, per image", huf: 20000, suffix: "perImage" },
          { name: "High-end Retouching and Object Replacement", detail: "Professional image editing", huf: 12000, suffix: "perHour" },
        ],
      },
    ],
    beautyItems: [
      { name: "Logo", required: false },
      { name: "Business card", required: true },
      { name: "Aftercare card / booklet", required: true },
      { name: "Gift card", required: true },
      { name: "Price list", required: true },
      { name: "Guest booklet", required: false },
      { name: "Brand identity", required: false },
    ],
    beautyTitle: "Beauty Industry Package",
    beautyDesc: "For beauty salons, nail artists, hairdressers, massage therapists, and every beauty professional",
    discounts: [
      { range: "For orders of 5–10 items", value: "−15% discount" },
      { range: "Above 10 items or monthly cooperation", value: "−25% discount" },
    ],
    conditions: [
      { icon: "ri-refresh-line", text: "Every price includes 3 revision rounds" },
      { icon: "ri-time-line", text: "Additional revisions: calculated from the hourly EUR price" },
      { icon: "ri-flashlight-line", text: "Rush fee within 48 hours: +50%" },
      { icon: "ri-price-tag-3-line", text: "Minimum project fee: calculated from the daily EUR rate" },
    ],
    process: [
      { num: "01", title: "Consultation", desc: "We clarify your goals, audience, and expectations." },
      { num: "02", title: "Design", desc: "Creative concepts, moodboard, and first visual directions." },
      { num: "03", title: "Iteration", desc: "Refinement based on feedback until the result feels right." },
      { num: "04", title: "Delivery", desc: "Final files, guidance, and long-term support." },
    ],
  },
} satisfies Record<Language, unknown>;

export const testimonialsContent = {
  hu: {
    eyebrow: "Ügyfélvélemények",
    titleTop: "Amit az ügyfelek",
    titleAccent: "mondanak",
    intro: "Valódi visszajelzések valódi emberektől. A minőség és az elégedettség a legfontosabb.",
    verified: "Valódi ügyfél",
    worksTitle: "Elkészült munkák",
    anonymousSuffix: "anonim ikon",
    stats: [
      { num: "30+", label: "Elégedett ügyfél" },
      { num: "30+", label: "Befejezett projekt" },
      { num: "100%", label: "Ajánlási arány" },
      { num: "3+", label: "Év tapasztalat" },
    ],
    testimonials: [
      { id: 6, name: "Nádasi Alexandra", role: "Tulajdonos", company: "Nalexa Beauty", avatar: "/testimonials/nalexa/profile.jpg", text: "🥰 Nagyon örülök, hogy veled dolgozhattam!\n\nAz arculat és a logó redesign pontosan olyan lett, amilyet elképzeltem: letisztult, elegáns és teljesen passzol a szalonom stílusához. Az árlisták is igényesen, átláthatóan készültek el, minden kérésemet figyelembe vette.\n\nKedves, gyors és precíz munkát végzett, bátran ajánlom mindenkinek! 🤍", rating: 5, service: "Szépségipari csomag", works: [{ image: "/testimonials/nalexa/logo-mockup.svg", label: "Logo mockup" }, { image: "/testimonials/nalexa/plakat-dizajn.svg", label: "Plakát dizájn" }, { image: "/testimonials/nalexa/logo-mockup-2.svg", label: "Logo mockup 2" }, { image: "/testimonials/nalexa/social-media-kit.svg", label: "Social media kit" }] },
      { id: 7, name: "Demeter Dorottya", role: "Tulajdonos", company: "Dorka Yoga", avatar: "/testimonials/dorka-yoga/profile.png", text: "Köszönöm @ivision_design-nak a Dorka Yoga arculatának és logójának megalkotását! 🤍\n\nIvett még egy jógaórámon is részt vett, hogy igazán átadja azt a hangulatot, amit elképzeltem, és ez tökéletesen sikerült. 🌿\n\nHa igényes, egyedi designt kerestek, szívből ajánlom! ✨", rating: 5, service: "Teljes arculattervezés", works: [{ image: "/testimonials/dorka-yoga/icon.png", label: "Logó ikon" }, { image: "/testimonials/dorka-yoga/logo-mockup.png", label: "Logó mockup" }, { image: "/testimonials/dorka-yoga/social-media-kit.png", label: "Social media kit" }, { image: "/testimonials/dorka-yoga/social-media-posts.png", label: "Social media posztok" }] },
      { id: 8, name: "Pávai Balázs", role: "Tulajdonos", company: "Forma Studio", avatar: "/testimonials/forma-studio/profile.png", text: "Néha a legjobb ötletek akkor születnek, amikor még nincs kész elképzelés.\n\nNagyon köszönöm Ivettnek a közös munkát. Kreativitásának köszönhetően olyan logó született, amely tökéletesen tükrözi a projekt világát/formáját.\n@ivision_design", rating: 5, service: "Logó- és arculattervezés", works: [{ image: "/testimonials/forma-studio/logo-mockup.png", label: "Logó mockup" }, { image: "/testimonials/forma-studio/social-kit.png", label: "Social kit" }, { image: "/testimonials/forma-studio/app-download-mockup.png", label: "Alkalmazás letöltés mockup" }, { image: "/testimonials/forma-studio/icon-mockup.png", label: "Ikon mockup" }] },
      { id: 3, name: "Végh Andrea", role: "Tulajdonos", company: "Party Land", text: "Ivett egyszerűen érzi a márkát. Nem csak szép dolgokat tervez, hanem olyan arculatot rak össze, ami tényleg mesél rólad. Gyors, kreatív és közben végig figyel arra, hogy te mit szeretnél átadni. Vele dolgozni nem stressz, hanem flow- és a végeredmény pont az, amit elképzeltél, csak még jobb :D", rating: 5, service: "Teljes arculattervezés és egyéb nyomdai anyagok", works: [{ image: "https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/bafdeda34308217c4dcd09239d18df27.png", label: "Logó & Arculat" }, { image: "https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/2e5dec7f6fce8a6497686a5c6366532b.png", label: "Nyomdai tervek" }] },
      { id: 1, name: "Andó Viktória", role: "Tulajdonos", company: "Andó Viki nails", text: "Nagyon lenyűgöző munkát kaptam. Az egyedi stílus és a színek használata igazán csodás. A részletekre való odafigyelés és a kreatív megoldások igazán profi szintet tükröznek. Imádom! Nagyon szépen köszönöm Ivett!", rating: 5, service: "Szépségipar Csomag", works: [{ image: "https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/613c6ed5b6c36167e9bdc5eadb2b4d8f.png", label: "Ajándékkártya" }, { image: "https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/c9f21eb7967033955b406cfb7ce4daa5.png", label: "Hűségkártya" }] },
      { id: 5, name: "Molnár Mercédesz", role: "Tulajdonos", company: "Molnár Merci beauty and makeup", text: "Nagyon köszönöm Ivettnek a közös munkát, hihetetlen gördülékenyen megy minden mindig, nagyon tehetséges és hamar megtaláltuk a közös hangot is! Kíváncsian várom a további közös ötleteléseket!", rating: 5, service: "Szépségipar Csomag", works: [{ image: "https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/d7839d5899c0207b80a3a6e4a8b8d1c5.png", label: "Logó-és Arculattervezés" }, { image: "https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/4a5f69701e4acb1cc4bc5b21891aad14.png", label: "Utóápolási füzet" }] },
      { id: 4, name: "Hevele István", role: "Tulajdonos", company: "traces.hu", text: "Ivettel dolgozni felüdülés. Proaktív hozzáállása részletekben gazdag és hatékony munkát eredményez. Többszöri együttműködésünk során rajzolódott ki, hogy az iVision az üzleti értékünk és víziónk, művészi kifejeződésének harmóniáját teremti meg.", rating: 5, service: "Teljes arculattervezés + névjegykártya", works: [{ image: "https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/774769371629938eac7f972c7e7b095f.png", label: "Logó" }, { image: "https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/4e066a97aaa68eb91b5f814acf93e913.png", label: "Névjegykártya" }] },
    ],
  },
  en: {
    eyebrow: "Client Reviews",
    titleTop: "What clients",
    titleAccent: "say",
    intro: "Real feedback from real people. Quality and satisfaction come first.",
    verified: "Verified client",
    worksTitle: "Completed work",
    anonymousSuffix: "anonymous icon",
    stats: [
      { num: "30+", label: "Happy clients" },
      { num: "30+", label: "Completed projects" },
      { num: "100%", label: "Recommendation rate" },
      { num: "3+", label: "Years experience" },
    ],
    testimonials: [
      { id: 6, name: "Nádasi Alexandra", role: "Owner", company: "Nalexa Beauty", avatar: "/testimonials/nalexa/profile.jpg", text: "🥰 I am so happy I got to work with you!\n\nThe brand identity and logo redesign turned out exactly as I imagined: clean, elegant, and a perfect fit for my salon's style. The price lists were also created with care and clarity, with every request of mine taken into consideration.\n\nYou worked kindly, quickly, and precisely. I wholeheartedly recommend you to everyone! 🤍", rating: 5, service: "Beauty industry package", works: [{ image: "/testimonials/nalexa/logo-mockup.svg", label: "Logo mockup" }, { image: "/testimonials/nalexa/plakat-dizajn.svg", label: "Poster design" }, { image: "/testimonials/nalexa/logo-mockup-2.svg", label: "Logo mockup 2" }, { image: "/testimonials/nalexa/social-media-kit.svg", label: "Social media kit" }] },
      { id: 7, name: "Demeter Dorottya", role: "Owner", company: "Dorka Yoga", avatar: "/testimonials/dorka-yoga/profile.png", text: "Thank you @ivision_design for creating the Dorka Yoga brand identity and logo! 🤍\n\nIvett even joined one of my yoga classes so she could truly capture the atmosphere I imagined, and she succeeded perfectly. 🌿\n\nIf you are looking for high-quality, unique design, I wholeheartedly recommend her! ✨", rating: 5, service: "Complete brand identity design", works: [{ image: "/testimonials/dorka-yoga/icon.png", label: "Logo icon" }, { image: "/testimonials/dorka-yoga/logo-mockup.png", label: "Logo mockup" }, { image: "/testimonials/dorka-yoga/social-media-kit.png", label: "Social media kit" }, { image: "/testimonials/dorka-yoga/social-media-posts.png", label: "Social media posts" }] },
      { id: 8, name: "Pávai Balázs", role: "Owner", company: "Forma Studio", avatar: "/testimonials/forma-studio/profile.png", text: "Sometimes the best ideas emerge before there is a clear vision.\n\nA huge thank you to Ivett for this collaboration. Her creativity brought to life a logo that perfectly captures the essence of the project.\n@ivision_design", rating: 5, service: "Logo and identity design", works: [{ image: "/testimonials/forma-studio/logo-mockup.png", label: "Logo mockup" }, { image: "/testimonials/forma-studio/social-kit.png", label: "Social kit" }, { image: "/testimonials/forma-studio/app-download-mockup.png", label: "App download mockup" }, { image: "/testimonials/forma-studio/icon-mockup.png", label: "Icon mockup" }] },
      { id: 3, name: "Végh Andrea", role: "Owner", company: "Party Land", text: "Ivett simply understands the brand. She does not just design beautiful things; she builds an identity that truly tells your story. She is fast, creative, and pays attention throughout to what you want to communicate. Working with her is not stressful, it is a flow, and the result is exactly what you imagined, only even better :D", rating: 5, service: "Complete brand identity and print materials", works: [{ image: "https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/bafdeda34308217c4dcd09239d18df27.png", label: "Logo & Identity" }, { image: "https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/2e5dec7f6fce8a6497686a5c6366532b.png", label: "Print designs" }] },
      { id: 1, name: "Andó Viktória", role: "Owner", company: "Andó Viki nails", text: "I received truly impressive work. The unique style and use of colors are wonderful. The attention to detail and creative solutions reflect a genuinely professional level. I love it. Thank you so much, Ivett!", rating: 5, service: "Beauty industry package", works: [{ image: "https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/613c6ed5b6c36167e9bdc5eadb2b4d8f.png", label: "Gift card" }, { image: "https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/c9f21eb7967033955b406cfb7ce4daa5.png", label: "Loyalty card" }] },
      { id: 5, name: "Molnár Mercédesz", role: "Owner", company: "Molnár Merci beauty and makeup", text: "Thank you so much to Ivett for our work together. Everything always goes incredibly smoothly, she is very talented, and we quickly found a shared rhythm. I am excited for more brainstorming together!", rating: 5, service: "Beauty industry package", works: [{ image: "https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/d7839d5899c0207b80a3a6e4a8b8d1c5.png", label: "Logo and identity design" }, { image: "https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/4a5f69701e4acb1cc4bc5b21891aad14.png", label: "Aftercare booklet" }] },
      { id: 4, name: "Hevele István", role: "Owner", company: "traces.hu", text: "Working with Ivett is refreshing. Her proactive attitude leads to detailed and efficient work. Through several collaborations, it became clear that iVision creates harmony between our business value, our vision, and their artistic expression.", rating: 5, service: "Complete brand identity + business card", works: [{ image: "https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/774769371629938eac7f972c7e7b095f.png", label: "Logo" }, { image: "https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/4e066a97aaa68eb91b5f814acf93e913.png", label: "Business card" }] },
    ],
  },
} satisfies Record<Language, unknown>;

export const colorsCopy = {
  hu: {
    defaults: [
      { name: "Menta Világos", role: "Háttér / Világos felület" },
      { name: "Türkiz Sötét", role: "Fő szín / Címek / CTA" },
      { name: "Pasztell Zöld", role: "Kiemelés / Másodlagos" },
      { name: "Sötét Szürke", role: "Szöveg / Kontraszt" },
    ],
    roles: ["Háttér / Világos felület", "Fő szín / Címek / CTA", "Kiemelés / Másodlagos", "Szöveg / Kontraszt"],
    fallbackRole: "Egyéb szín",
    colorName: (index: number) => `${index + 1}. szín`,
    quickPair: (a: number, b: number) => `${a}. + ${b}. szín`,
    invalid: "Érvénytelen Coolors link. Pl.: https://coolors.co/ddece8-004643-acd1c6-202020",
    eyebrow: "Színpaletta Segítő",
    titleTop: "Találd meg a",
    titleAccent: "márkád színeit",
    intro: "Generálj palettát a Coolors.co-n, illeszd be a linket, és az előnézet azonnal frissül.",
    paste: "Illeszd be a Coolors.co linkedet",
    apply: "Alkalmaz",
    success: "Paletta sikeresen betöltve! Az összes előnézet frissült.",
    open: "Nyisd meg a Coolors generátort",
    hint: "Változtass a palettán, másold ki az URL-t, illeszd be ide",
    adjust: "Szín finomhangolás",
    modified: "Módosított",
    lightness: "Világosság",
    saturation: "Telítettség",
    reset: "Visszaállítás",
    preview: "Élő előnézet",
    sampleCompany: "Minta Kft.",
    sampleService: "Grafikai tervezés",
    sampleEmail: "hello@minta.hu",
    mainButton: "Fő gomb",
    secondaryButton: "Másodlagos",
    bgColor: "Háttér szín",
    textColor: "Szöveg szín",
    contrast: "Kontraszt ellenőrző",
    wcag: "WCAG 2.1 szabvány",
    headline: "Címsor szöveg",
    paragraph: "Ez egy normál bekezdés, amit az ügyfelek fognak olvasni.",
    contrastRatio: "Kontraszt arány",
    rating: "Értékelés",
    contrastGood: "Kiváló olvashatóság. Minden méretű szöveghez ajánlott.",
    contrastOk: "Nagyobb méretű szöveghez (18px+) megfelelő. Címekhez használható.",
    contrastBad: "Gyenge kontraszt. Kerüld ezt a kombinációt fő szövegeknél.",
    quickPairs: "Gyors párosítások",
  },
  en: {
    defaults: [
      { name: "Light Mint", role: "Background / Light surface" },
      { name: "Deep Teal", role: "Main color / Titles / CTA" },
      { name: "Pastel Green", role: "Highlight / Secondary" },
      { name: "Dark Gray", role: "Text / Contrast" },
    ],
    roles: ["Background / Light surface", "Main color / Titles / CTA", "Highlight / Secondary", "Text / Contrast"],
    fallbackRole: "Additional color",
    colorName: (index: number) => `Color ${index + 1}`,
    quickPair: (a: number, b: number) => `Color ${a} + ${b}`,
    invalid: "Invalid Coolors link. Example: https://coolors.co/ddece8-004643-acd1c6-202020",
    eyebrow: "Color Palette Helper",
    titleTop: "Find your",
    titleAccent: "brand colors",
    intro: "Generate a palette on Coolors.co, paste the link, and the preview updates instantly.",
    paste: "Paste your Coolors.co link",
    apply: "Apply",
    success: "Palette loaded successfully. All previews were updated.",
    open: "Open Coolors generator",
    hint: "Adjust the palette, copy the URL, then paste it here",
    adjust: "Color fine-tuning",
    modified: "Modified",
    lightness: "Lightness",
    saturation: "Saturation",
    reset: "Reset",
    preview: "Live preview",
    sampleCompany: "Sample Ltd.",
    sampleService: "Graphic design",
    sampleEmail: "hello@example.com",
    mainButton: "Primary button",
    secondaryButton: "Secondary",
    bgColor: "Background color",
    textColor: "Text color",
    contrast: "Contrast checker",
    wcag: "WCAG 2.1 standard",
    headline: "Headline text",
    paragraph: "This is a normal paragraph that clients will read.",
    contrastRatio: "Contrast ratio",
    rating: "Rating",
    contrastGood: "Excellent readability. Recommended for every text size.",
    contrastOk: "Suitable for larger text (18px+). Good for headings.",
    contrastBad: "Weak contrast. Avoid this combination for body text.",
    quickPairs: "Quick pairings",
  },
} satisfies Record<Language, unknown>;

export const contact = {
  hu: {
    aria: "Kapcsolatfelvétel",
    ctaAlt: "CTA háttér",
    bannerTop: "Indítsuk el a következő",
    bannerAccent: "sikertörténetet!",
    bannerText: "Legyen szó brandingről vagy grafikai tervezésről – szívesen hallom az ötletedet.",
    bannerButton: "Vedd fel a kapcsolatot",
    eyebrow: "Kapcsolat",
    titleTop: "Írj nekem,",
    titleAccent: "válaszolok!",
    intro: "Minden projekt egy új lehetőség. Mesélj a céljaidról, és közösen kitaláljuk, hogyan valósíthatjuk meg a legjobban.",
    social: "Közösségi média",
    labels: { name: "Név *", phone: "Telefon *", email: "E-mail cím *", brand: "Márkanév *", logo: "Logó", website: "Weboldal", optional: "(opcionális)", service: "Szolgáltatás *", style: "Stílus irányzat *", message: "Üzenet" },
    placeholders: { name: "Kovács Anna", email: "anna@pelda.hu", brand: "Cég vagy márka neve", website: "https://pelda.hu", service: "Válassz szolgáltatást...", message: "Mesélj a projektedről, céljaidról, határidőkről, inspirációkról..." },
    logoOptions: [
      { value: "", label: "Van-e már logód?" },
      { value: "Nincs még logó", label: "Nincs még logó – újat szeretnék" },
      { value: "Van logó, de újítanék", label: "Van logó, de újítanék" },
      { value: "Van logó, megtartanám", label: "Van logó, megtartanám" },
    ],
    styleOptions: ["Minimalista", "Modern", "Körlogó", "Iniciálé", "Vintage / Retro", "Luxus / Elegáns", "Játékos / Kreatív", "Geometrikus", "Kézzel rajzolt", "Más – leírom az üzenetben"],
    serviceOptions: ["Logó Design Pro", "Arculati Kézikönyv", "Full Brand Identity Csomag", "Névjegykártya tervezés", "Szórólap / Árlista design", "Plakát / Molinó tervezés", "Ajándékutalvány / Névtábla / Menükártya", "Social Media poszt", "Egyedi termékdesign (textil, bögre, mágnes)", "Egyedi AI Illusztráció / Koncepcióalkotás", "Termékfotó AI környezetbe helyezés", "High-end retusálás és objektum csere", "Szépségipar Csomag", "Egyéb / Nem tudom még"],
    success: "Üzeneted megérkezett! Hamarosan felveszem veled a kapcsolatot.",
    error: "Hiba történt. Kérlek próbáld újra, vagy írj e-mailt.",
    sending: "Küldés...",
    submit: "Üzenet küldése",
  },
  en: {
    aria: "Contact",
    ctaAlt: "CTA background",
    bannerTop: "Let's start the next",
    bannerAccent: "success story!",
    bannerText: "Whether it is branding or graphic design, I would love to hear your idea.",
    bannerButton: "Get in touch",
    eyebrow: "Contact",
    titleTop: "Write to me,",
    titleAccent: "I will reply!",
    intro: "Every project is a new opportunity. Tell me about your goals, and we will find the best way to bring them to life together.",
    social: "Social media",
    labels: { name: "Name *", phone: "Phone *", email: "Email address *", brand: "Brand name *", logo: "Logo", website: "Website", optional: "(optional)", service: "Service *", style: "Style direction *", message: "Message" },
    placeholders: { name: "Anna Smith", email: "anna@example.com", brand: "Company or brand name", website: "https://example.com", service: "Choose a service...", message: "Tell me about your project, goals, deadline, and inspirations..." },
    logoOptions: [
      { value: "", label: "Do you already have a logo?" },
      { value: "No logo yet - I want a new one", label: "No logo yet - I want a new one" },
      { value: "I have a logo but want a redesign", label: "I have a logo but want a redesign" },
      { value: "I have a logo and want to keep it", label: "I have a logo and want to keep it" },
    ],
    styleOptions: ["Minimalist", "Modern", "Circular logo", "Initials", "Vintage / Retro", "Luxury / Elegant", "Playful / Creative", "Geometric", "Hand-drawn", "Other - I will describe it in the message"],
    serviceOptions: ["Logo Design Pro", "Brand Guidelines", "Full Brand Identity Package", "Business Card Design", "Flyer / Price List Design", "Poster / Banner Design", "Gift Voucher / Name Sign / Menu Card", "Social Media Post", "Custom Product Design", "Custom AI Illustration / Concept Creation", "Product Photo in AI Environment", "High-end Retouching and Object Replacement", "Beauty Industry Package", "Other / Not sure yet"],
    success: "Your message has arrived. I will contact you soon.",
    error: "Something went wrong. Please try again or send me an email.",
    sending: "Sending...",
    submit: "Send message",
  },
} satisfies Record<Language, unknown>;

export const footer = {
  hu: {
    text: "Prémium design megoldások, amelyek látomásból valóságot teremtenek. Branding és grafika – minden részletben.",
    groups: [
      { title: "Szolgáltatások", links: [{ label: "Branding & Arculat", href: "#services" }, { label: "Grafikai tervezés", href: "#services" }, { label: "AI & Fotómanipuláció", href: "#services" }] },
      { title: "Portfólió", links: [{ label: "Branding munkák", href: "#portfolio" }, { label: "Print anyagok", href: "#portfolio" }] },
      { title: "Jogi dokumentumok", links: [{ label: "Adatkezelési tájékoztató", href: "/ivision-adatkezelesi-tajekoztato.pdf", external: true }] },
      { title: "Kapcsolat", links: [{ label: "Konzultáció kérése", href: "#contact" }, { label: common.email, href: `mailto:${common.email}` }, { label: common.site, href: baseUrl }] },
    ],
    rights: "Minden jog fenntartva.",
    credit: "Tervezte & fejlesztette:",
  },
  en: {
    text: "Premium design solutions that turn vision into reality. Branding and graphics in every detail.",
    groups: [
      { title: "Services", links: [{ label: "Branding & Identity", href: "#services" }, { label: "Graphic design", href: "#services" }, { label: "AI & Photo manipulation", href: "#services" }] },
      { title: "Portfolio", links: [{ label: "Branding work", href: "#portfolio" }, { label: "Print materials", href: "#portfolio" }] },
      { title: "Legal", links: [{ label: "Privacy Notice", href: "/ivision-adatkezelesi-tajekoztato.pdf", external: true }] },
      { title: "Contact", links: [{ label: "Request a consultation", href: "#contact" }, { label: common.email, href: `mailto:${common.email}` }, { label: common.site, href: baseUrl }] },
    ],
    rights: "All rights reserved.",
    credit: "Designed & developed by:",
  },
} satisfies Record<Language, unknown>;

export const seo = {
  hu: {
    title: "iVision Design – Prémium Branding & Grafikai Tervezés",
    description: "iVision Design: Prémium branding, logó tervezés és grafikai tervezés. 3+ év tapasztalat. Arculat, social media grafika, AI illusztráció és nyomdai tervek – látomásból valóság.",
    keywords: "branding, logó tervezés, grafikai tervezés, UI/UX, arculattervezés, social media grafika, AI illusztráció, névjegykártya tervezés, nyomdai tervezés",
    ogTitle: "iVision Design – Prémium Design Stúdió",
    ogDescription: "Prémium branding, logó tervezés és grafikai tervezés. Arculat, social media grafika, AI illusztráció és nyomdai tervek.",
    locale: "hu_HU",
    catalogName: "Grafikai tervezési szolgáltatások",
    faq: [
      { q: "Mennyibe kerül egy logó tervezés?", a: "A Logó Design Pro csomag ára 55 000 Ft, amely 3 koncepciót, 3 módosítást és vektoros forrásfájlokat tartalmaz." },
      { q: "Mit tartalmaz a Full Brand Identity Csomag?", a: "A Full Brand Identity Csomag tartalmazza a logót és variációit, arculati kézikönyvet, névjegykártyát, Social Media kitet és ajándék matrica designt." },
      { q: "Hány módosítás szerepel az árban?", a: "Minden ár 3 módosítási kört tartalmaz. További javítások ára 10 000 Ft/óra." },
    ],
  },
  en: {
    title: "iVision Design – Premium Branding & Graphic Design",
    description: "iVision Design offers premium branding, logo design, brand identity, social media graphics, AI illustration, and print design with a refined visual approach.",
    keywords: "branding, logo design, graphic design, UI/UX, brand identity, social media graphics, AI illustration, business card design, print design",
    ogTitle: "iVision Design – Premium Design Studio",
    ogDescription: "Premium branding, logo design, brand identity, social media graphics, AI illustration, and print design.",
    locale: "en_US",
    catalogName: "Graphic design services",
    faq: [
      { q: "How much does logo design cost?", a: "Logo Design Pro is converted from the HUF base price to EUR using the daily exchange rate and rounded upward to .99." },
      { q: "What is included in the Full Brand Identity Package?", a: "It includes logo design and variations, brand guidelines, a business card, a social media kit, and a free sticker design." },
      { q: "How many revisions are included?", a: "Every price includes 3 revision rounds. Additional revisions are calculated from the hourly EUR price." },
    ],
  },
} satisfies Record<Language, unknown>;
